const MILLISECONDS_PER_DAY = 86_400_000;

export const DEFAULT_DAILY_TIME_ZONE = "Europe/Amsterdam";

export interface DailyDrawOptions {
  /** Separates independent rotations, for example `reflection-card` and `recipe`. */
  channel: string;
  date?: Date;
  timeZone?: string;
}

export interface DailyDrawResult<T> {
  item: T;
  dayKey: string;
  /** Zero-based position in the stable ID-sorted collection. */
  position: number;
  collectionSize: number;
}

interface IdentifiedItem {
  id: string;
}

function assertValidDate(date: Date) {
  if (Number.isNaN(date.getTime())) {
    throw new RangeError("De datum voor de dagselectie is ongeldig.");
  }
}

/** Returns a YYYY-MM-DD key for the calendar day in the chosen time zone. */
export function toDayKey(
  date: Date,
  timeZone = DEFAULT_DAILY_TIME_ZONE,
): string {
  assertValidDate(date);

  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const values = Object.fromEntries(
    parts
      .filter((part) => part.type === "year" || part.type === "month" || part.type === "day")
      .map((part) => [part.type, part.value]),
  );

  if (!values.year || !values.month || !values.day) {
    throw new RangeError("De kalenderdag kon niet worden bepaald.");
  }

  return `${values.year}-${values.month}-${values.day}`;
}

function dayOrdinal(dayKey: string): number {
  const [yearText, monthText, dayText] = dayKey.split("-");
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  return Math.floor(Date.UTC(year, month - 1, day) / MILLISECONDS_PER_DAY);
}

/** FNV-1a gives a compact, stable unsigned hash without runtime randomness. */
function stableHash(value: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function greatestCommonDivisor(left: number, right: number): number {
  let a = Math.abs(left);
  let b = Math.abs(right);
  while (b !== 0) {
    const remainder = a % b;
    a = b;
    b = remainder;
  }
  return a;
}

function rotationStep(collectionSize: number, channel: string): number {
  if (collectionSize <= 2) return 1;

  let candidate = (stableHash(`${channel}:step`) % (collectionSize - 1)) + 1;
  while (greatestCommonDivisor(candidate, collectionSize) !== 1) {
    candidate = (candidate % (collectionSize - 1)) + 1;
  }
  return candidate;
}

function positiveModulo(value: number, modulus: number): number {
  return ((value % modulus) + modulus) % modulus;
}

/**
 * Selects one shared gift for a local calendar day.
 *
 * The collection is sorted by stable ID before selection, so editorial
 * reordering has no effect. A channel-specific offset and coprime step create
 * a full rotation: refreshes never change the draw and no item repeats until
 * every item in that collection has appeared once.
 */
export function drawDaily<T extends IdentifiedItem>(
  items: readonly T[],
  {
    channel,
    date = new Date(),
    timeZone = DEFAULT_DAILY_TIME_ZONE,
  }: DailyDrawOptions,
): DailyDrawResult<T> {
  if (items.length === 0) {
    throw new RangeError("Een dagselectie heeft minstens één item nodig.");
  }
  if (channel.trim().length === 0) {
    throw new TypeError("Een dagselectie heeft een stabiel kanaal nodig.");
  }

  const dayKey = toDayKey(date, timeZone);
  const stableItems = [...items].sort((left, right) =>
    left.id.localeCompare(right.id, "en"),
  );
  const offset = stableHash(`${channel}:offset`) % stableItems.length;
  const step = rotationStep(stableItems.length, channel);
  const position = positiveModulo(
    offset + dayOrdinal(dayKey) * step,
    stableItems.length,
  );
  const item = stableItems[position];

  if (!item) {
    throw new RangeError("De dagselectie viel buiten de collectie.");
  }

  return {
    item,
    dayKey,
    position,
    collectionSize: stableItems.length,
  };
}
